/**
 * The Contract — LegalFlare's canonical glossary, on the compile path
 * (issue #696).
 *
 * WHY TYPESCRIPT
 *   tsc is the only gate CI runs on every PR. `as const` yields literal-id
 *   unions with no codegen, so a compute path typed `Record<MeasureId, …>`
 *   makes a missing measure TS2741 and an invented one TS2820 — a type
 *   error before it is ever a wrong number. A JSON canon cannot do this
 *   (compiler-verified: a JSON import accepts a made-up id, and Zod
 *   re-widens the literals — see the hand-annotated `readonly string[]`
 *   at packages/shared/src/registry/registry.ts ATOM_IDS [pointer corrected
 *   2026-08-26, #764 s06 clean-check repair — was ":256", past EOF; named by
 *   symbol 2026-10-09, #1179 — the line ":127" moved when the revisions
 *   schema was added above it].
 *   Generate toward the weaker consumer, never
 *   toward the enforcer.
 *
 * WHAT BINDS WHERE
 *   - Ids: the exported unions below, re-exported through
 *     packages/shared/src/index.ts and available for `Record<…Id, …>`
 *     typing [corrected 2026-08-26, #764 s06: no consumer is typed that
 *     way today — the mechanism is offered, not yet exercised].
 *   - Populations: imported predicate symbols (predicates.ts), never strings.
 *   - Relational invariants (alias uniqueness, roster membership, money
 *     restatement): packages/shared/src/contract.schema.ts (Zod), run by
 *     the test suite — it validates CONTRACT and never reassigns from
 *     parse(), so the literals survive.
 *
 * HOW IT GROWS
 *   One pass per entry, per engineering/contract/PASS.md — the procedure,
 *   gates, and queue order live there, against inventory.ts's frozen
 *   39-event denominator. Shards cut the merge surface; only one thread
 *   edits this folder at a time.
 */
import { DIMENSIONS } from "./dimensions";
import { FACTS } from "./facts";
import { MEASURES } from "./measures";
import { PROVENANCE } from "./provenance";
import { RETIRED } from "./retired";
import { TERMS } from "./terms";

export const CONTRACT = {
  dimensions: DIMENSIONS,
  facts: FACTS,
  measures: MEASURES,
  terms: TERMS,
  retired: RETIRED,
  // What happened to the data — one entry per happening; facts' segments point
  // at these by id (#890; the word is W3C PROV's).
  provenance: PROVENANCE,
} as const;

/** The literal-id unions — the enforcement surface (E1).
 *  RETIRED is deliberately absent from every one of them: a retired term is
 *  readable in the canon and unnameable in code. That is what makes the
 *  retirement real rather than a note. */
export type MeasureId = (typeof MEASURES)[number]["id"];
export type DimensionId = (typeof DIMENSIONS)[number]["id"];
export type ProcessId = (typeof FACTS)[number]["id"];
export type TermId = (typeof TERMS)[number]["id"];
export type ProvenanceId = (typeof PROVENANCE)[number]["id"];

/** Provenance entries keyed by id, literal types intact — how an actor reads
 *  a happening's date instead of typing its own copy (book-predicates.ts reads
 *  the ledger's ship day and the Rejected Date completeness day here). */
export const PROVENANCE_BY_ID = Object.fromEntries(
  PROVENANCE.map((p) => [p.id, p]),
) as { [E in (typeof PROVENANCE)[number] as E["id"]]: E };

/**
 * Measures keyed by id, each entry keeping its OWN literal type — so
 * `MEASURE_BY_ID["placement.time_to_decision"].population` is the exact
 * declared predicate, not the widened `(row: never) => boolean` of the
 * `MeasureEntry` shape.
 *
 * THIS IS WHAT MAKES `population` LOAD-BEARING (added 2026-08-09, PR #706
 * review). Until now a compute imported its predicate directly and the canon's
 * declaration was checked by nothing: swapping `population: isFirmDecision` to
 * `isHouseFirm` left tsc and all 152 shared tests green, so an entry could
 * claim a population the code did not use — the contract recording an
 * intention while a reader trusted it over the code. A compute that filters
 * through this map cannot do that: declare a predicate that does not accept
 * the rows being filtered and the call site fails TS2345 (verified by doing
 * exactly that before wiring it).
 *
 * The honest limit: a compute reading its predicate from here is bound, one
 * importing the symbol directly is not, and the canon cannot tell them apart.
 * So this is a pattern every measure pass must follow — written into PASS.md
 * §5 under "Read the population back through MEASURE_BY_ID" — not a guarantee
 * the type system supplies unasked.
 */
export const MEASURE_BY_ID = Object.fromEntries(
  MEASURES.map((m) => [m.id, m]),
) as { [E in (typeof MEASURES)[number] as E["id"]]: E };

/** The money facts (PASS.md §0.5 rule 2 — enumerated, not inferred).
 *  These run last and need Chris. */
export const MONEY_FACT_IDS = [
  "fee_split_recorded",
  "hf_disbursement_confirmed",
  "lf_payment_received",
  "of_payment_received",
  "bank_deposit_cleared",
  "settlement_reported",
  "settlement_amended",
  "settlement_doc_filed",
] as const;

export { DIMENSIONS, FACTS, MEASURES, PROVENANCE, RETIRED, TERMS };
export * from "./predicates";
export type * from "./types";
